import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import { alpha, useTheme } from '@mui/material/styles'
import { fmtCount, fmtMinutes, fmtSeconds } from '../StreamIngest/vocabulary'
import { STAR_HINTS } from './hints'

// The flow's building blocks, shared by both rows' panels (README.md): a
// stage (`Node`), the hop between two (`Pipe`), a row's `Panel`, its
// sentence, the product's own number (`StarMetric`) and the layout that turns
// the rows from left to right to top to bottom (`FlowLayout`). Nothing here
// knows a number of its own; the panels hand every one in.
//
// Latency is the emphasis and is deliberately NOT judged. The docs set no
// targets yet, so a pipe is green when audio moved through it in the window
// with nothing lost, never because a number was under some bar this page made
// up.

// The narrowest a row may be before it turns to run top to bottom: six stages
// at their narrowest and the five pipes between them.
const ROW_MIN_PX = 1040

const Vertical = createContext(false)

// The flow's own width, so the rows turn on the room they have rather than
// the window's, which also holds the nav.
const useWidth = () => {
  const ref = useRef(null)
  const [width, setWidth] = useState(() => window.innerWidth)
  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return [ref, width]
}

// The account a page is narrowed to, as its URL carries it: `user` (the id)
// and `name` (the filter's label). The Overview, Pipeline, Loop and Moments
// pages all read these, so a link between them keeps the account.
export const accountParams = (user) =>
  user
    ? { user: user.id, ...(user.username ? { name: user.username } : {}) }
    : {}

// The account a URL named, as the filter holds one.
export const accountFrom = (searchParams) => {
  const id = searchParams.get('user')
  return id ? { id, username: searchParams.get('name') || undefined } : null
}

// `path` on `user`'s account, its query kept and any `#section` left last.
// No account, the path as given.
export const withAccount = (path, user) => {
  if (!path || !user) return path
  const [base, hash] = path.split('#')
  const [pathname, query] = base.split('?')
  const params = new URLSearchParams(query)
  Object.entries(accountParams(user)).forEach(([key, value]) =>
    params.set(key, value)
  )
  return `${pathname}?${params}${hash ? `#${hash}` : ''}`
}

// Scrolls to the section a stage's link named (`/pipeline#capture`) once the
// page has drawn it: `ready` is true when the read the section waits on has
// landed. A router link changes the hash without the browser's own jump.
export const useScrollToHash = (ready) => {
  const { hash } = useLocation()
  useEffect(() => {
    if (!ready || !hash) return
    document
      .getElementById(decodeURIComponent(hash.slice(1)))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash, ready])
}

// Status → color, from the theme so it matches every other tone on the page.
const useTone = () => {
  const theme = useTheme()
  return (status) =>
    ({
      ok: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      idle: theme.palette.text.disabled,
      pending: theme.palette.text.disabled,
    }[status] || theme.palette.text.disabled)
}

// One stage. The top border carries its status; the big number is the one
// thing to read; the lines under it are the plain-words context; an alert chip
// is the only thing rendered in a hot color, so red on this view always means
// "something is wrong here" and never "this number is large".
//
// A **gate** is a stage where audio forks on purpose, and what it turned aside
// hangs beneath the card as a `diversion` — in grey, never red, because
// skipped audio is the gate doing its job and it is still stored. A gate that
// runs in shadow (it measures, drops nothing) draws its diversion dotted and
// says "would": the level gate today, the media gate when it lands. Any future
// gate is one more node with the same slot.
const CHIP_POP_MS = 600

const Diversion = ({ diversion }) => {
  if (!diversion) return null
  const { label, lines = [], shadow = false, hint } = diversion
  return (
    <Tooltip title={hint || ''} placement="bottom" arrow>
      <Box
        sx={{
          mt: 0.75,
          ml: 2,
          pl: 1.25,
          borderLeft: `2px ${shadow ? 'dotted' : 'solid'}`,
          borderColor: 'text.disabled',
          color: 'text.secondary',
          cursor: 'default',
        }}
      >
        <Typography
          variant="caption"
          display="block"
          sx={{ fontWeight: 600, fontStyle: shadow ? 'italic' : 'normal' }}
        >
          ↳ {label}
        </Typography>
        {lines.map((line) => (
          <Typography key={line} variant="caption" display="block">
            {line}
          </Typography>
        ))}
      </Box>
    </Tooltip>
  )
}

// `link` is `{ to, label }`: the panel elsewhere that explains this stage,
// a line at the card's foot.
const Node = ({
  icon,
  title,
  value,
  lines = [],
  alert,
  status,
  hint,
  chips = null,
  diversions = [],
  link = null,
}) => {
  const tone = useTone()
  const vertical = useContext(Vertical)
  const pending = status === 'pending'
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(vertical
          ? { width: '100%' }
          : { flex: '1 1 140px', minWidth: 120, maxWidth: 260 }),
      }}
    >
      <Tooltip title={hint || ''} placement="top" arrow>
        <Paper
          variant="outlined"
          sx={{
            p: vertical ? 1.5 : 2,
            borderRadius: 2,
            borderTop: `3px solid ${tone(status)}`,
            borderStyle: pending ? 'dashed' : 'solid',
            opacity: pending ? 0.6 : 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {icon} {title}
          </Typography>
          <Typography
            variant={vertical ? 'h5' : 'h4'}
            fontWeight={700}
            sx={{ my: 0.25 }}
          >
            {value}
          </Typography>
          {lines.map((line) => (
            <Typography
              key={line}
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {line}
            </Typography>
          ))}
          {alert && (
            <Chip
              size="small"
              color={alert.tone}
              label={alert.text}
              // Wraps rather than cutting off: a narrow card must still say
              // what is wrong.
              sx={{
                mt: 1,
                fontWeight: 600,
                maxWidth: '100%',
                height: 'auto',
                py: 0.25,
                '& .MuiChip-label': { whiteSpace: 'normal' },
              }}
            />
          )}
          {link && (
            <Link
              component={RouterLink}
              to={link.to}
              variant="caption"
              display="block"
              underline="hover"
              sx={{ mt: 1, fontWeight: 600 }}
            >
              {link.label}&nbsp;→
            </Link>
          )}
          {chips && chips.items.length > 0 && (
            // What the router heard this tick: one chip per class, sized by the
            // minutes it was heard for, keyed on the tick so a fresh payload pops
            // them in again and a repeated one leaves them still. An aggregate
            // over everyone routed in the minute — no account, no content.
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {chips.items.map((chip, index) => (
                <Chip
                  key={`${chips.id}-${chip.index}`}
                  size="small"
                  variant="outlined"
                  label={`${chip.name} · ${fmtMinutes(chip.ms)}`}
                  sx={{
                    fontSize: 11,
                    height: 22,
                    animation: `chip-pop ${CHIP_POP_MS}ms ease-out ${
                      index * 90
                    }ms both`,
                    '@keyframes chip-pop': {
                      from: { transform: 'scale(0.6)', opacity: 0 },
                      to: { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Tooltip>
      {diversions.filter(Boolean).map((diversion) => (
        <Diversion key={diversion.label} diversion={diversion} />
      ))}
    </Box>
  )
}

// The hop between two stages. Three things are encoded and they are kept
// apart on purpose, because they answer three different questions:
//
// * **color** is health over the window — green when audio moved through
//   with nothing lost, red when something fell out (and hangs below);
// * **the burst** is one refresh's worth of events — on each tick the pipe
//   sends across exactly the spans declared (first pipe) or the segments a
//   seal verified (second pipe) since the previous tick, one dash each, once.
//   Between ticks the track is bare. Three new spans is three dashes
//   crossing; a quiet minute is nothing moving, which is what a quiet minute
//   looks like;
// * **the caption** says the same thing in words, and — when a tick is too
//   big to draw one-for-one — how many events a dash stands for.
//
// Two earlier versions were wrong in instructive ways. The first animated a
// gradient whenever anything had flowed in the *window*, so a pipe could run
// for a week after the last byte. The second drew today's total as standing
// dashes, so motion and count were decoupled from the moment. A conveyor of
// this tick's events is the only version where what you see moving is what
// just happened.
//
// Running top to bottom, the track stands up beside its words and the dashes
// fall down it; everything else is the same pipe.
// Every duration on this panel wears it; nothing else does. The pending
// pipes carry a step name rather than a time, so they stay bare — which is
// how a glance tells a measured hop from one that is not built yet.
const STOPWATCH = '⏱'

const Duration = ({ children }) => (
  <>
    <Box component="span" sx={{ mr: 0.4, opacity: 0.75 }} aria-hidden>
      {STOPWATCH}
    </Box>
    {children}
  </>
)

const MAX_DASHES = 24
const CROSS_MS = 1600
const STAGGER_MS = 110
const DASH_PX = 14
// The standing track's height, run top to bottom.
const DROP_PX = 44

const perDash = (count) =>
  count == null || count <= MAX_DASHES ? 1 : Math.ceil(count / MAX_DASHES)

const Pipe = ({ status, label, hint, leak, burst, unit }) => {
  const tone = useTone()
  const vertical = useContext(Vertical)
  const color = tone(status)
  const active = status === 'ok' || status === 'warning' || status === 'error'
  const count = burst?.count ?? null
  // At the server's cap the count is a floor, so the caption says "2,000+"
  // rather than implying the fleet produced exactly the cap.
  const atLeast = Boolean(burst?.truncated)
  const scale = perDash(count)
  const dashes = count == null || count <= 0 ? 0 : Math.ceil(count / scale)
  // Keyed on the tick, so a new payload remounts the dashes and the crossing
  // replays; the same payload twice (the server cache answering both polls)
  // changes nothing and nothing moves, which is correct — nothing new happened.
  const tick = burst?.id || 'none'
  const caption =
    !active || count == null
      ? null
      : count === 0
      ? `none since last refresh`
      : `${fmtCount(count)}${atLeast ? '+' : ''} ${unit} this tick${
          scale > 1 ? ` · 1 dash ≈ ${scale}` : ''
        }`

  const track = (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        ...(vertical
          ? { width: 6, height: DROP_PX }
          : {
              flex: 1,
              height: 6,
              // A size container, so the crossing distance below can be
              // written in `cqw` — the track's own width — instead of
              // measuring it.
              containerType: 'inline-size',
            }),
        backgroundColor:
          status === 'pending' ? 'transparent' : alpha(color, 0.18),
        ...(status === 'pending'
          ? vertical
            ? { borderLeft: `2px dashed ${color}` }
            : { borderTop: `2px dashed ${color}` }
          : {}),
      }}
    >
      {active &&
        Array.from({ length: dashes }, (_, index) => (
          <Box
            key={`${tick}-${index}`}
            sx={{
              position: 'absolute',
              borderRadius: 3,
              bgcolor: color,
              ...(vertical
                ? { left: 0, width: 6, height: DASH_PX, top: -DASH_PX }
                : { top: 0, height: 6, width: DASH_PX, left: -DASH_PX }),
              animation: `flow-cross ${CROSS_MS}ms linear ${
                index * STAGGER_MS
              }ms 1 forwards`,
              '@keyframes flow-cross': {
                from: { transform: 'translate(0, 0)' },
                // The track's own length plus the dash, so it fully exits.
                to: {
                  transform: vertical
                    ? `translateY(${DROP_PX + DASH_PX}px)`
                    : `translateX(calc(100cqw + ${DASH_PX}px))`,
                },
              },
            }}
          />
        ))}
    </Box>
  )
  const head = (
    <Box
      sx={{
        width: 0,
        height: 0,
        ...(vertical
          ? {
              mt: 0.25,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: `9px solid ${
                status === 'pending' ? alpha(color, 0.6) : color
              }`,
            }
          : {
              ml: 0.25,
              borderTop: '7px solid transparent',
              borderBottom: '7px solid transparent',
              borderLeft: `9px solid ${
                status === 'pending' ? alpha(color, 0.6) : color
              }`,
            }),
      }}
    />
  )
  const words = (
    <>
      <Tooltip title={hint || ''} placement="top" arrow>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            mb: vertical ? 0 : 0.75,
            color: active ? 'text.primary' : 'text.disabled',
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      {vertical ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {track}
          {head}
        </Box>
      )}
      <Typography
        variant="caption"
        sx={{
          mt: vertical ? 0 : 0.75,
          minHeight: vertical ? 0 : 18,
          color: count ? 'text.secondary' : 'text.disabled',
        }}
      >
        {caption || (vertical ? null : ' ')}
      </Typography>
      {(leak || !vertical) && (
        <Typography
          variant="caption"
          sx={{
            minHeight: vertical ? 0 : 18,
            color: leak ? 'error.main' : 'transparent',
            fontWeight: leak ? 600 : 400,
          }}
        >
          {leak || '·'}
        </Typography>
      )}
    </>
  )

  if (vertical)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 0.5,
          }}
        >
          {track}
          {head}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>{words}</Box>
      </Box>
    )
  return (
    <Box
      sx={{
        flex: '1 1 80px',
        minWidth: 64,
        maxWidth: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        px: 1,
        // Level with the cards' numbers, not the middle of the tallest stage,
        // which its diversions can make three times the height of the rest.
        alignSelf: 'flex-start',
        mt: 6,
      }}
    >
      {words}
    </Box>
  )
}

// A row's verdict in one plain sentence. Its status is the colour of the edge
// beside it: the panel's, or a summary line's own.
const Sentence = ({ verdict, variant = 'h5' }) => (
  <Typography variant={variant} fontWeight={700}>
    {verdict.text}
  </Typography>
)

// A row's sentence on a phone's summary, at the top of the page: the same
// coloured edge as its panel, short.
const SummaryLine = ({ verdict }) => {
  const tone = useTone()
  return (
    <Box sx={{ borderLeft: `4px solid ${tone(verdict.status)}`, pl: 1.5 }}>
      <Sentence verdict={verdict} variant="h6" />
    </Box>
  )
}

// One row of the flow in its own panel: its name, which links to the page the
// row is computed from; its status as the panel's coloured edge; its sentence
// and anything beside it, unless the page's summary carries them; the stages.
const Panel = ({ title, to, status, verdict, aside, children }) => {
  const tone = useTone()
  const vertical = useContext(Vertical)
  return (
    <Paper
      variant="outlined"
      sx={{
        p: vertical ? 2 : 3,
        borderRadius: 2,
        mb: 3,
        borderLeft: `6px solid ${tone(status)}`,
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ lineHeight: 1.6, mb: 0.75 }}
      >
        {to ? (
          <Link component={RouterLink} to={to} color="inherit">
            {title}
          </Link>
        ) : (
          title
        )}
      </Typography>
      {(verdict || aside) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          {verdict && <Sentence verdict={verdict} />}
          <Box sx={{ flex: 1 }} />
          {aside}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: vertical ? 'column' : 'row',
          alignItems: 'stretch',
          gap: vertical ? 0.5 : 0,
        }}
      >
        {children}
      </Box>
    </Paper>
  )
}

// The product's own number: moment → push (step 4b), the end of a moment in
// the room to the push about it, the window's median with p90 beside it and
// end of recording → sealed, the transport's share, beneath. Where no push was
// measured (develop sends none), end of recording → sealed itself. An API from
// before the window's lags were sent falls back to the newest day's.
const StarMetric = ({ payload }) => {
  const vertical = useContext(Vertical)
  const transport =
    payload?.window?.transport || payload?.days?.[0]?.transport || null
  const lag = (key, p = 'p50_s') => transport?.[key]?.[p] ?? null
  const product =
    payload?.product?.window || payload?.product?.days?.[0] || null
  const momentToPush = product?.moment_to_push?.p50_s ?? null
  const endToSafe = lag('end_to_sealed')
  return momentToPush != null ? (
    <Tooltip title={STAR_HINTS.momentToPush} placement="left" arrow>
      <Box sx={{ textAlign: vertical ? 'left' : 'right' }}>
        <Typography variant="h4" fontWeight={700} component="span">
          <Duration>{fmtSeconds(momentToPush)}</Duration>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="span"
          sx={{ ml: 1 }}
        >
          moment → push · p90{' '}
          {fmtSeconds(product?.moment_to_push?.p90_s ?? null)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          end of recording → sealed <Duration>{fmtSeconds(endToSafe)}</Duration>
        </Typography>
      </Box>
    </Tooltip>
  ) : (
    <Tooltip title={STAR_HINTS.endToSafe} placement="left" arrow>
      <Box sx={{ textAlign: vertical ? 'left' : 'right' }}>
        <Typography variant="h4" fontWeight={700} component="span">
          <Duration>{fmtSeconds(endToSafe)}</Duration>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="span"
          sx={{ ml: 1 }}
        >
          end of recording → sealed · p90{' '}
          {fmtSeconds(lag('end_to_sealed', 'p90_s'))}
        </Typography>
      </Box>
    </Tooltip>
  )
}

// The panels' common frame: measures the room it has and tells every stage,
// pipe and panel inside whether the rows run left to right or top to bottom.
// Measured outside the panels, whose padding changes with the direction: a
// width read inside one would flip the rows back and forth at the edge.
const FlowLayout = ({ children }) => {
  const [measured, width] = useWidth()
  return (
    <Vertical.Provider value={width < ROW_MIN_PX}>
      <Box ref={measured}>{children}</Box>
    </Vertical.Provider>
  )
}

const useVertical = () => useContext(Vertical)

export {
  Duration,
  FlowLayout,
  Node,
  Panel,
  Pipe,
  Sentence,
  StarMetric,
  SummaryLine,
  useTone,
  useVertical,
}
